import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  Settings,
  User,
  Lock,
  Bell,
  Globe,
  Save,
  Shield,
  Eye,
  EyeOff,
  Check,
  Loader,
  Camera,
  Sliders
} from 'lucide-react';
import profileService from '../services/profileService';
import './SettingsPage.css';

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { admin, updateAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    name: admin?.name || '',
    email: admin?.email || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notifications, setNotifications] = useState({
    newUser: true,
    newOffer: true,
    newRequest: true,
    reports: true,
    emailNotifications: true
  });

  const [matchingWindowDays, setMatchingWindowDays] = useState(90);
  const [matchingWindowError, setMatchingWindowError] = useState('');
  const [matchingWindowSaving, setMatchingWindowSaving] = useState(false);
  const [matchingWindowSaved, setMatchingWindowSaved] = useState(false);

  const [whatsappMessage, setWhatsappMessage] = useState('مرحبا {clientName} لدينا عرض يناسب طلبكم\n{offerTitle}\n{offerLink}');
  const [whatsappMessageSaving, setWhatsappMessageSaving] = useState(false);
  const [whatsappMessageSaved, setWhatsappMessageSaved] = useState(false);

  const [offerStaleDays, setOfferStaleDays] = useState(30);
  const [offerStaleDaysError, setOfferStaleDaysError] = useState('');
  const [offerStaleDaysSaving, setOfferStaleDaysSaving] = useState(false);
  const [offerStaleDaysSaved, setOfferStaleDaysSaved] = useState(false);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setSettingsLoading(true);
        const [profileRes, siteRes] = await Promise.all([
          profileService.getSettings(),
          profileService.getSiteSettings()
        ]);
        if (profileRes.success && profileRes.data?.notificationSettings) {
          setNotifications(profileRes.data.notificationSettings);
        }
        if (siteRes.success && siteRes.data?.matchingWindowDays) {
          setMatchingWindowDays(siteRes.data.matchingWindowDays);
        }
        if (siteRes.success && siteRes.data?.whatsappDefaultMessage) {
          setWhatsappMessage(siteRes.data.whatsappDefaultMessage);
        }
        if (siteRes.success && siteRes.data?.offerStaleDays) {
          setOfferStaleDays(siteRes.data.offerStaleDays);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleWhatsappMessageSave = async () => {
    setWhatsappMessageSaving(true);
    try {
      const res = await profileService.updateSiteSettings({ whatsappDefaultMessage: whatsappMessage });
      if (res.success) {
        setWhatsappMessageSaved(true);
        setTimeout(() => setWhatsappMessageSaved(false), 2500);
      }
    } catch (err) {
      console.error('Error saving whatsapp message:', err);
    } finally {
      setWhatsappMessageSaving(false);
    }
  };

  const handleOfferStaleDaysSave = async () => {
    const val = Number(offerStaleDays);
    if (!Number.isInteger(val) || val < 1) {
      setOfferStaleDaysError('يجب أن يكون الرقم عدداً صحيحاً أكبر من 0');
      return;
    }
    setOfferStaleDaysError('');
    setOfferStaleDaysSaving(true);
    try {
      const res = await profileService.updateSiteSettings({ offerStaleDays: val });
      if (res.success) {
        setOfferStaleDaysSaved(true);
        setTimeout(() => setOfferStaleDaysSaved(false), 2500);
      } else {
        setOfferStaleDaysError(res.message || 'حدث خطأ أثناء الحفظ');
      }
    } catch (err) {
      setOfferStaleDaysError('حدث خطأ أثناء الحفظ');
    } finally {
      setOfferStaleDaysSaving(false);
    }
  };

  const handleMatchingWindowSave = async () => {
    const val = Number(matchingWindowDays);
    if (!Number.isInteger(val) || val < 1) {
      setMatchingWindowError('يجب أن يكون الرقم عدداً صحيحاً أكبر من 0');
      return;
    }
    setMatchingWindowError('');
    setMatchingWindowSaving(true);
    try {
      const res = await profileService.updateSiteSettings({ matchingWindowDays: val });
      if (res.success) {
        setMatchingWindowSaved(true);
        setTimeout(() => setMatchingWindowSaved(false), 2500);
      } else {
        setMatchingWindowError(res.message || 'حدث خطأ أثناء الحفظ');
      }
    } catch (err) {
      setMatchingWindowError('حدث خطأ أثناء الحفظ');
    } finally {
      setMatchingWindowSaving(false);
    }
  };

  // Handle avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('errors.invalidFileType', 'يرجى اختيار صورة صالحة'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('errors.fileTooLarge', 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت'));
      return;
    }

    setAvatarUploading(true);
    setError(null);

    try {
      const response = await profileService.uploadAvatar(file);
      if (response.success) {
        const adminData = response.data.admin || response.data;
        updateAdmin(adminData);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleProfileSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    
    try {
      const response = await profileService.updateProfile(profileData);
      if (response.success) {
        updateAdmin(response.data.admin);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  }, [profileData, updateAdmin, t]);

  const handlePasswordSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('errors.passwordMismatch'));
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setError(t('errors.passwordTooShort', 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'));
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await profileService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.success) {
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  }, [passwordData, t]);

  const handleNotificationChange = async (key) => {
    const newValue = !notifications[key];
    setNotifications(prev => ({ ...prev, [key]: newValue }));
    
    // Save to backend
    try {
      await profileService.updateNotificationSettings({ [key]: newValue });
    } catch (err) {
      // Revert on error
      setNotifications(prev => ({ ...prev, [key]: !newValue }));
      console.error('Error saving notification setting:', err);
    }
  };

  const handleLanguageChange = async (lang) => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    localStorage.setItem('language', lang);
    
    // Save to backend
    try {
      await profileService.updatePreferences({ language: lang });
    } catch (err) {
      console.error('Error saving language preference:', err);
    }
  };

  const tabs = [
    { id: 'profile', label: t('settings.profile'), icon: User },
    { id: 'security', label: t('settings.security'), icon: Lock },
    { id: 'notifications', label: t('settings.notifications'), icon: Bell },
    { id: 'language', label: t('settings.language'), icon: Globe },
    { id: 'advanced', label: 'متقدم', icon: Sliders }
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <div className="header-title">
          <Settings size={28} />
          <h1>{t('settings.title')}</h1>
        </div>
      </div>

      <div className="settings-container">
        {/* Tabs */}
        <div className="settings-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-section">
              <div className="section-header-settings">
                <h2>{t('settings.profileSettings')}</h2>
                <p>{t('settings.profileDescription')}</p>
              </div>

              <form onSubmit={handleProfileSubmit}>
                <div className="profile-avatar-section">
                  <div className="avatar-large">
                    {(admin?.avatarUrl || admin?.avatar) ? (
                      <img src={admin.avatarUrl || admin.avatar} alt={admin.name} />
                    ) : (
                      <span>{admin?.name?.charAt(0)}</span>
                    )}
                    {avatarUploading && (
                      <div className="avatar-loading">
                        <Loader className="spin" size={24} />
                      </div>
                    )}
                  </div>
                  <div className="avatar-info">
                    <h3>{admin?.name}</h3>
                    <span className="admin-badge">
                      <Shield size={14} />
                      {t('common.superAdmin')}
                    </span>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAvatarChange}
                    />
                    <button 
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={avatarUploading}
                      style={{ marginTop: '10px' }}
                    >
                      <Camera size={16} />
                      {avatarUploading ? t('common.saving', 'جاري الرفع...') : t('settings.changeAvatar', 'تغيير الصورة')}
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('settings.name')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={profileData.name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.email')}</label>
                    <input
                      type="email"
                      className="form-input"
                      value={profileData.email}
                      onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  {error && activeTab === 'profile' && (
                    <div className="error-message" style={{ color: '#ef4444', marginBottom: '10px' }}>
                      {error}
                    </div>
                  )}
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                    {t('common.save')}
                  </button>
                  {saved && (
                    <span className="saved-message">
                      <Check size={16} />
                      {t('settings.saved')}
                    </span>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-section">
              <div className="section-header-settings">
                <h2>{t('settings.changePassword')}</h2>
                <p>{t('settings.passwordDescription')}</p>
              </div>

              <form onSubmit={handlePasswordSubmit}>
                <div className="form-group">
                  <label className="form-label">{t('settings.currentPassword')}</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      className="form-input"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('settings.newPassword')}</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        className="form-input"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.confirmPassword')}</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    <Lock size={18} />
                    {t('settings.updatePassword')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <div className="section-header-settings">
                <h2>{t('settings.notificationSettings')}</h2>
                <p>{t('settings.notificationDescription')}</p>
              </div>

              {settingsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Loader className="spin" size={32} />
                </div>
              ) : (
                <div className="notification-options">
                  <div className="notification-option">
                    <div className="option-info">
                      <span className="option-label">{t('settings.newUserNotification')}</span>
                      <span className="option-description">{t('settings.newUserNotificationDesc')}</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notifications.newUser}
                        onChange={() => handleNotificationChange('newUser')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-option">
                    <div className="option-info">
                      <span className="option-label">{t('settings.newOfferNotification')}</span>
                      <span className="option-description">{t('settings.newOfferNotificationDesc')}</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notifications.newOffer}
                        onChange={() => handleNotificationChange('newOffer')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-option">
                    <div className="option-info">
                      <span className="option-label">{t('settings.newRequestNotification')}</span>
                      <span className="option-description">{t('settings.newRequestNotificationDesc')}</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notifications.newRequest}
                        onChange={() => handleNotificationChange('newRequest')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-option">
                    <div className="option-info">
                      <span className="option-label">{t('settings.emailNotifications')}</span>
                      <span className="option-description">{t('settings.emailNotificationsDesc')}</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notifications.emailNotifications}
                        onChange={() => handleNotificationChange('emailNotifications')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Language Tab */}
          {activeTab === 'language' && (
            <div className="settings-section">
              <div className="section-header-settings">
                <h2>{t('settings.languageSettings')}</h2>
                <p>{t('settings.languageDescription')}</p>
              </div>

              <div className="language-options">
                <button
                  className={`language-option ${i18n.language === 'ar' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('ar')}
                >
                  <span className="lang-flag">🇸🇦</span>
                  <span className="lang-name">العربية</span>
                  {i18n.language === 'ar' && <Check size={18} className="check-icon" />}
                </button>

                <button
                  className={`language-option ${i18n.language === 'en' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('en')}
                >
                  <span className="lang-flag">🇺🇸</span>
                  <span className="lang-name">English</span>
                  {i18n.language === 'en' && <Check size={18} className="check-icon" />}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="settings-section">
              <div className="section-header-settings">
                <h2>إعدادات متقدمة</h2>
                <p>إعدادات النظام الداخلية للمنصة</p>
              </div>

              <div className="form-group" style={{ maxWidth: '400px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  مدة صلاحية العروض والطلبات للمطابقة (بالأيام)
                </label>
                <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
                  العروض والطلبات التي لم تُحدَّث خلال هذه المدة لن تدخل في المطابقة التلقائية.
                </p>
                <input
                  type="number"
                  min="1"
                  value={matchingWindowDays}
                  onChange={(e) => {
                    setMatchingWindowDays(e.target.value);
                    setMatchingWindowError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: matchingWindowError ? '1px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '15px',
                    direction: 'ltr',
                    textAlign: 'center'
                  }}
                />
                {matchingWindowError && (
                  <p style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px' }}>
                    {matchingWindowError}
                  </p>
                )}
                <button
                  onClick={handleMatchingWindowSave}
                  disabled={matchingWindowSaving}
                  className="btn-save"
                  style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {matchingWindowSaving ? (
                    <><Loader size={16} className="spin" /> جارٍ الحفظ...</>
                  ) : matchingWindowSaved ? (
                    <><Check size={16} /> تم الحفظ</>
                  ) : (
                    <><Save size={16} /> حفظ</>
                  )}
                </button>
              </div>

              <div className="form-group" style={{ maxWidth: '400px', marginTop: '32px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  عدد الأيام قبل تحويل العرض إلى غير محدث
                </label>
                <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
                  بعد انقضاء هذه المدة من آخر تحديث للعرض، يتحول تلقائياً إلى حالة "غير محدث".
                </p>
                <input
                  type="number"
                  min="1"
                  value={offerStaleDays}
                  onChange={(e) => {
                    setOfferStaleDays(e.target.value);
                    setOfferStaleDaysError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: offerStaleDaysError ? '1px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '15px',
                    direction: 'ltr',
                    textAlign: 'center'
                  }}
                />
                {offerStaleDaysError && (
                  <p style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px' }}>
                    {offerStaleDaysError}
                  </p>
                )}
                <button
                  onClick={handleOfferStaleDaysSave}
                  disabled={offerStaleDaysSaving}
                  className="btn-save"
                  style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {offerStaleDaysSaving ? (
                    <><Loader size={16} className="spin" /> جارٍ الحفظ...</>
                  ) : offerStaleDaysSaved ? (
                    <><Check size={16} /> تم الحفظ</>
                  ) : (
                    <><Save size={16} /> حفظ</>
                  )}
                </button>
              </div>

              <div className="form-group" style={{ maxWidth: '500px', marginTop: '32px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  رسالة واتساب الافتراضية عند إرسال عرض للعميل
                </label>
                <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
                  استخدم المتغيرات التالية في الرسالة:<br />
                  <code style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: '4px' }}>{'{clientName}'}</code> — اسم العميل &nbsp;
                  <code style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: '4px' }}>{'{offerTitle}'}</code> — عنوان العرض &nbsp;
                  <code style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: '4px' }}>{'{offerLink}'}</code> — رابط العرض
                </p>
                <textarea
                  value={whatsappMessage}
                  onChange={(e) => setWhatsappMessage(e.target.value)}
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: 'rtl',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={handleWhatsappMessageSave}
                  disabled={whatsappMessageSaving}
                  className="btn-save"
                  style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {whatsappMessageSaving ? (
                    <><Loader size={16} className="spin" /> جارٍ الحفظ...</>
                  ) : whatsappMessageSaved ? (
                    <><Check size={16} /> تم الحفظ</>
                  ) : (
                    <><Save size={16} /> حفظ الرسالة</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
